import {
  differenceInCalendarDays,
  format,
  parseISO,
  startOfDay,
} from 'date-fns'

import { EMPTY_LIST } from '../../constants/common'
import { DATE_API_DATA_FORMAT_SHORT } from '../../constants/date'
import { ROUTE_PATH_LIST_RENTAL_PORTAL } from '../../constants/route'
import {
  getSectionCanViewPermission,
  isValueStringType,
} from '../../utils/common'
import { toUkTimezoneDate, transposeToUkTimezoneDate } from '../../utils/date'
import {
  getListingPortalLabel,
  getListingPortalLogoUrl,
} from '../RentalPortalListing'

import {
  PORTAL_AVATAR_TEXT,
  RENTAL_PORTAL_SUBSECTION_LIST,
} from './RentalPortal.constants'

export function getRentalPortalSubsectionLink(subsectionName) {
  return `/${ROUTE_PATH_LIST_RENTAL_PORTAL.root}/${subsectionName}`
}

export function getRentalPortalViewableSubsections(sections) {
  return RENTAL_PORTAL_SUBSECTION_LIST.filter(subsection =>
    getSectionCanViewPermission(sections, subsection.uiSectionName)
  )
}

export function getRentalPortalDefaultSubsectionName(sections) {
  return (
    getRentalPortalViewableSubsections(sections)[0] ||
    RENTAL_PORTAL_SUBSECTION_LIST[0]
  ).name
}

export function getRentalPortalSubsectionByPathname(pathname) {
  return RENTAL_PORTAL_SUBSECTION_LIST.find(subsection =>
    pathname.startsWith(getRentalPortalSubsectionLink(subsection.name))
  )
}

export function getIsListingAvailableImmediately({ availableFrom, timezone }) {
  return (
    !availableFrom ||
    differenceInCalendarDays(
      parseListingDayInAppTimezone(availableFrom, timezone),
      toUkTimezoneDate(new Date(), timezone)
    ) <= 0
  )
}

export function parseListingFormDate(value, timezone) {
  if (!value) {
    return null
  }

  if (!isValueStringType(value)) {
    return value
  }

  return timezone ? toUkTimezoneDate(value, timezone) : parseISO(value)
}

export function parseListingDayInAppTimezone(value, timezone) {
  const date = parseListingFormDate(value)

  return date ? transposeToUkTimezoneDate(startOfDay(date), timezone) : date
}

export function getListingDateValue(value) {
  return value ? format(value, DATE_API_DATA_FORMAT_SHORT) : null
}

export function getRentalPortalBoardSelectionState({
  getIsSelectable,
  items,
  selectedIds,
}) {
  const selectableItems = items.filter(getIsSelectable)

  const selectedCount = selectableItems.filter(item =>
    selectedIds.includes(item.id)
  ).length

  return {
    isAllSelected:
      selectableItems.length > 0 && selectedCount === selectableItems.length,
    isPartlySelected:
      selectedCount > 0 && selectedCount < selectableItems.length,
    selectableCount: selectableItems.length,
    selectableItems,
  }
}

export function getRentalPortalBoardNextSelection({
  isChecked,
  items,
  selected,
}) {
  const itemIds = items.map(item => item.id)

  const rest = selected.filter(item => !itemIds.includes(item.id))

  return isChecked ? [...rest, ...items] : rest
}

export function getPortalAvatarText(portal) {
  return PORTAL_AVATAR_TEXT[portal] ?? portal
}

export function getPortalAvatarProps(portal) {
  const label = getListingPortalLabel(portal)

  return {
    alt: label,
    src: getListingPortalLogoUrl(portal),
    text: getPortalAvatarText(label),
  }
}

export function getListingProfileTabsWithAttentionDot({
  dotClassName,
  preparedTabs,
  tabsAwaitingYou = EMPTY_LIST,
  tooltip,
}) {
  return preparedTabs.map(tab =>
    tabsAwaitingYou.includes(tab.id)
      ? {
          ...tab,
          text: (
            <>
              {tab.text}
              <span className={dotClassName} />
            </>
          ),
          tooltip,
        }
      : tab
  )
}

export function getRentalPortalSectionSubsections(subsections) {
  return subsections.filter(subsection => !subsection.isInSettingsAccordion)
}

export function getRentalPortalSettingsSubsections(subsections) {
  return subsections.filter(subsection => subsection.isInSettingsAccordion)
}

export function getHasRentalPortalSettingsSubsections(settingsSubsections) {
  return settingsSubsections.length > 0
}
